// src/business/pages/inventory/StockOverview.jsx
import { Fragment, useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpDown, ArrowDownToLine, Search, Package, ChevronRight, ChevronDown, ShoppingCart } from "lucide-react";
import Swal from "../../../utils/apparifySwal";
import { inventoryAPI, logisticsAPI, productsAPI } from "../../services/businessErpService";
import { PageHeader, DataTable, Pagination, StatusBadge, FormModal, BtnPrimary, BtnSecondary } from "../../components/erp";
import OpenWorkPanel from "../../components/OpenWorkPanel";
import Panel from "../../components/Panel";
import { businessSwalBase } from "../../utils/businessSwal";
import { getDeliveryLabel, getPOLabel } from "../../../utils/documentLabels";
import ShopSelect from "../../../components/ShopSelect";

const inputCls =
  "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mnla-accent dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100";

// -- Stock level status badge ----------------------------------
function resolveStockStatus(quantity, threshold = 5) {
  if (Number(quantity || 0) <= 0) return "out_of_stock";
  if (Number(quantity || 0) <= Number(threshold || 5)) return "low_stock";
  return "in_stock";
}

function StockStatusBadge({ quantity, threshold = 5, status }) {
  const resolvedStatus = status || resolveStockStatus(quantity, threshold);
  if (resolvedStatus === "out_of_stock") return <span className="inline-flex items-center rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-semibold text-rose-800 dark:bg-rose-950 dark:text-rose-200">Out of Stock</span>;
  if (resolvedStatus === "low_stock") return <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-950 dark:text-amber-200">Low Stock</span>;
  return <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">In Stock</span>;
}

function deriveGroupStockSummary(group) {
  const activeVariants = (group?.variants || []).filter((variant) => Number(variant.variant_active ?? 1) !== 0);
  const variants = activeVariants.length ? activeVariants : (group?.variants || []);
  const normalized = variants.map((variant) => {
    const quantity = Number(variant.quantity ?? 0);
    const threshold = Number(variant.low_stock_threshold ?? group?.low_stock_threshold ?? 5);
    return {
      quantity,
      status: resolveStockStatus(quantity, threshold),
    };
  });

  const outOfStockCount = normalized.filter((variant) => variant.status === "out_of_stock").length;
  const lowStockCount = normalized.filter((variant) => variant.status === "low_stock").length;
  const inStockCount = normalized.filter((variant) => variant.status === "in_stock").length;

  let status = "in_stock";
  if (!normalized.length || inStockCount === 0) status = "out_of_stock";
  else if (outOfStockCount > 0 || lowStockCount > 0) status = "low_stock";

  return {
    status,
    variantCount: variants.length,
    outOfStockCount,
    lowStockCount,
    inStockCount,
  };
}

function describeGroupStockSummary(summary) {
  if (!summary.variantCount) return "No tracked variants";
  if (summary.status === "out_of_stock") return "All tracked variants are out of stock";
  if (summary.outOfStockCount > 0 || summary.lowStockCount > 0) {
    const parts = [];
    if (summary.outOfStockCount > 0) parts.push(`${summary.outOfStockCount} out`);
    if (summary.lowStockCount > 0) parts.push(`${summary.lowStockCount} low`);
    if (summary.inStockCount > 0) parts.push(`${summary.inStockCount} healthy`);
    return parts.join(" | ");
  }
  return `${summary.inStockCount} healthy variant${summary.inStockCount === 1 ? "" : "s"}`;
}

const ADJUST_STATUSES = ["DRAFT", "LISTED"];

function adjustVariantLabel(v) {
  if (v.optionValues && typeof v.optionValues === "object" && Object.keys(v.optionValues).length)
    return Object.values(v.optionValues).join(" / ");
  return v.title || v.sku || null;
}

function toWholeNumberInput(value) {
  return String(value ?? "").replace(/[^\d]/g, "");
}

function toReceiveInputValue(value, zeroAsBlank = true) {
  const numeric = Number(value || 0);
  if (!numeric && zeroAsBlank) return "";
  return String(Math.max(numeric, 0));
}

function buildReceiveQuantityPreset(line, quantity) {
  const outstanding = Math.max(Number(line.outstandingQuantity || 0), 0);
  const safeQuantity = Math.min(Math.max(Number(quantity || 0), 0), outstanding);
  return {
    ...line,
    receivedQuantity: toReceiveInputValue(safeQuantity),
    acceptedQuantity: toReceiveInputValue(safeQuantity),
    rejectedQuantity: safeQuantity > 0 ? "0" : "",
    discrepancyReason: "",
  };
}

function normalizeReceiveLineDraft(line, updates = {}, changedField = null) {
  const next = { ...line, ...updates };
  const outstanding = Math.max(Number(line.outstandingQuantity || 0), 0);
  let received = Math.min(Number(toWholeNumberInput(next.receivedQuantity) || 0), outstanding);
  let accepted = Math.min(Number(toWholeNumberInput(next.acceptedQuantity) || 0), received);
  let rejected = Math.min(Number(toWholeNumberInput(next.rejectedQuantity) || 0), received);

  if (changedField === "receivedQuantity") {
    accepted = received;
    rejected = 0;
  } else if (changedField === "acceptedQuantity") {
    rejected = Math.max(received - accepted, 0);
  } else if (changedField === "rejectedQuantity") {
    accepted = Math.max(received - rejected, 0);
  } else if (accepted + rejected > received) {
    rejected = Math.max(received - accepted, 0);
  }

  if (received === 0) {
    accepted = 0;
    rejected = 0;
  }

  return {
    ...next,
    receivedQuantity: toReceiveInputValue(received),
    acceptedQuantity: toReceiveInputValue(accepted),
    rejectedQuantity: received > 0 ? toReceiveInputValue(rejected, false) : "",
    discrepancyReason: rejected > 0 ? next.discrepancyReason : "",
  };
}

function buildReceiveLines(delivery) {
  return (delivery?.lines || []).map((line) => {
    const expectedQuantity = Number(line.expectedQuantity || line.expected_quantity || 0);
    const receivedToDate = Number(line.receivedQuantity || line.received_quantity || 0);
    const baseLine = {
      deliveryLineId: Number(line.id),
      purchaseOrderLineId: Number(line.purchaseOrderLineId || line.purchase_order_line_id),
      productId: Number(line.productId || line.product_id),
      variantId: line.variantId || line.variant_id || "",
      label: line.variantTitle || line.variant_title || "Default",
      sku: line.variantSku || line.variant_sku || "",
      expectedQuantity,
      receivedToDate,
      acceptedToDate: Number(line.acceptedQuantity || line.accepted_quantity || 0),
      rejectedToDate: Number(line.rejectedQuantity || line.rejected_quantity || 0),
      outstandingQuantity: Math.max(expectedQuantity - receivedToDate, 0),
      receivedQuantity: "",
      acceptedQuantity: "",
      rejectedQuantity: "",
      discrepancyReason: "",
      note: "",
    };
    return buildReceiveQuantityPreset(baseLine, baseLine.outstandingQuantity);
  });
}

function summarizeReceiveLines(lines) {
  return lines.reduce((acc, line) => {
    const received = Number(line.receivedQuantity || 0);
    const accepted = Number(line.acceptedQuantity || 0);
    const rejected = Number(line.rejectedQuantity || 0);
    if (received > 0) acc.lineCount += 1;
    acc.received += received;
    acc.accepted += accepted;
    acc.rejected += rejected;
    return acc;
  }, { lineCount: 0, received: 0, accepted: 0, rejected: 0 });
}

export default function StockOverview({
  title = "Inventory",
  subtitle = "Track stock levels, movements, receipts, and adjustments",
  initialTab = "levels",
  lockedTab = null,
  movementTypeFilter = null,
  showOpenWork = true,
  showAdjustAction = true,
  showReceiveAction = true,
  historyTitle = null,
} = {}) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(lockedTab || initialTab);
  const [movements, setMovements] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [stockLevels, setStockLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Search / filter for stock levels tab
  const [searchQ, setSearchQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Adjust stock form
  const [showAdjust, setShowAdjust] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ productId: "", variantId: "", adjustmentQty: "", reason: "" });
  const [adjusting, setAdjusting] = useState(false);

  // Receive stock form
  const [showReceive, setShowReceive] = useState(false);
  const [receiveForm, setReceiveForm] = useState({
    deliveryId: "",
    purchaseOrderId: "",
    notes: "",
  });
  const [receiveLines, setReceiveLines] = useState([]);
  const [selectedDeliveryDetail, setSelectedDeliveryDetail] = useState(null);
  const [loadingReceiveDetail, setLoadingReceiveDetail] = useState(false);
  const [receiving, setReceiving] = useState(false);

  // Delivered deliveries for receive dropdown
  const [deliveredList, setDeliveredList] = useState([]);
  const [deliveryOpenWork, setDeliveryOpenWork] = useState([]);
  // Products for adjust dropdown
  const [allProducts, setAllProducts] = useState([]);

  // Grouped stock expansion + contextual adjustment
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [adjustContext, setAdjustContext] = useState(null);

  useEffect(() => {
    const nextTab = lockedTab || initialTab;
    setTab(nextTab);
    setPage(1);
  }, [initialTab, lockedTab]);

  // Group stock levels by product
  const groupedStock = useMemo(() => {
    const groups = {};
    for (const row of stockLevels) {
      const pid = row.product_id;
      if (!groups[pid]) {
        groups[pid] = {
          product_id: pid,
          product_name: row.product_name,
          product_status: row.product_status,
          low_stock_threshold: row.low_stock_threshold ?? 5,
          variants: [],
          totalQty: 0,
          totalReserved: 0,
          totalIncoming: 0,
        };
      }
      groups[pid].variants.push(row);
      groups[pid].totalQty += (row.quantity ?? 0);
      groups[pid].totalReserved += (row.reserved ?? 0);
      groups[pid].totalIncoming += (row.incoming ?? 0);
    }
    return Object.values(groups);
  }, [stockLevels]);

  useEffect(() => {
    const validIds = new Set(groupedStock.map((group) => group.product_id));
    setExpandedProducts((prev) => {
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [groupedStock]);

  const toggleProduct = (pid) => {
    setExpandedProducts(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "levels") {
        const res = await inventoryAPI.listStockLevels({ page, limit: 20, q: searchQ || undefined, status: statusFilter || undefined });
        setStockLevels(res?.data || []);
        setTotalPages(res?.pagination?.totalPages || 1);
      } else if (tab === "movements") {
        const res = await inventoryAPI.listMovements({ page, limit: 20, movementType: movementTypeFilter || undefined });
        setMovements(res?.data || res?.items || []);
        setTotalPages(res?.pagination?.totalPages || res?.meta?.totalPages || 1);
      } else {
        const res = await inventoryAPI.listReceipts({ page, limit: 20 });
        setReceipts(res?.data || res?.items || []);
        setTotalPages(res?.pagination?.totalPages || res?.meta?.totalPages || 1);
      }
    } catch {
      if (tab === "levels") setStockLevels([]);
      else if (tab === "movements") setMovements([]);
      else setReceipts([]);
    } finally {
      setLoading(false);
    }
  }, [tab, page, searchQ, statusFilter, movementTypeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    logisticsAPI.list({ limit: 100 })
      .then((res) => {
        const rows = res?.data?.deliveries || res?.data || [];
        setDeliveryOpenWork(Array.isArray(rows) ? rows : []);
      })
      .catch(() => setDeliveryOpenWork([]));
  }, []);

  // Load delivered deliveries when receive modal opens
  useEffect(() => {
    if (!showReceive) return;
    logisticsAPI.list({ status: "DELIVERED", limit: 100 })
      .then((res) => {
        const rows = res?.data?.deliveries || res?.data || [];
        const openRows = rows.filter((row) =>
          ["PENDING_RECEIPT", "PARTIALLY_RECEIVED", "RECEIVED_WITH_DISCREPANCY"].includes(row.receiving_status || "PENDING_RECEIPT")
        );
        const sorted = [...openRows].sort((a, b) => {
          const aOpen = ["PENDING_RECEIPT", "PARTIALLY_RECEIVED", "RECEIVED_WITH_DISCREPANCY"].includes(a.receiving_status) ? 0 : 1;
          const bOpen = ["PENDING_RECEIPT", "PARTIALLY_RECEIVED", "RECEIVED_WITH_DISCREPANCY"].includes(b.receiving_status) ? 0 : 1;
          if (aOpen !== bOpen) return aOpen - bOpen;
          return Number(b.id) - Number(a.id);
        });
        setDeliveredList(sorted);
      })
      .catch(() => setDeliveredList([]));
  }, [showReceive]);

  // Load products when adjust modal opens (include DRAFT for procurement relevance)
  // Navigate to PR creation with product/variant context
  const createPRForProduct = (productId, variantId = null) => {
    const params = new URLSearchParams({ prefillProductId: String(productId) });
    if (variantId) params.set("prefillVariantId", String(variantId));
    navigate(`/business/procurement/requisitions?${params.toString()}`);
  };

  const [productVariants, setProductVariants] = useState([]);
  useEffect(() => {
    if (!showAdjust) return;
    if (adjustContext) return;
    Promise.all(ADJUST_STATUSES.map(s => productsAPI.list({ limit: 200, status: s })))
      .then(results => {
        const merged = results.flatMap(r => r?.data || r?.products || []);
        setAllProducts(merged);
      })
      .catch(() => setAllProducts([]));
    setProductVariants([]);
  }, [showAdjust, adjustContext]);

  // Load variants when product is selected in adjust modal
  useEffect(() => {
    if (!adjustForm.productId) { setProductVariants([]); return; }
    productsAPI.getById(adjustForm.productId)
      .then((res) => {
        const variants = res?.data?.variants || res?.variants || [];
        setProductVariants(variants);
      })
      .catch(() => setProductVariants([]));
  }, [adjustForm.productId]);

  const swalBase = useMemo(() => ({ ...businessSwalBase, target: "#business-swal-root" }), []);
  const receiveSummary = useMemo(() => summarizeReceiveLines(receiveLines), [receiveLines]);
  const inventoryOpenWorkItems = useMemo(() => {
    const lowStockVariants = stockLevels.filter((row) => Number(row.quantity ?? 0) > 0 && Number(row.quantity ?? 0) <= Number(row.low_stock_threshold ?? 5)).length;
    const outOfStockVariants = stockLevels.filter((row) => Number(row.quantity ?? 0) <= 0).length;
    const pendingReceipts = deliveryOpenWork.filter((row) =>
      ["PENDING_RECEIPT", "PARTIALLY_RECEIVED", "RECEIVED_WITH_DISCREPANCY"].includes(String(row.receiving_status || "").toUpperCase()),
    );
    const discrepancyReceipts = deliveryOpenWork.filter((row) =>
      String(row.receiving_status || "").toUpperCase() === "RECEIVED_WITH_DISCREPANCY",
    );
    return [
      lowStockVariants ? {
        id: "inventory-low-stock",
        title: "Low-stock variants",
        description: "These variants are below their configured reorder threshold.",
        count: lowStockVariants,
        tone: "amber",
        onClick: () => setStatusFilter("low_stock"),
        actionLabel: "Filter stock levels",
      } : null,
      outOfStockVariants ? {
        id: "inventory-out-stock",
        title: "Out-of-stock variants",
        description: "Variants with zero on-hand quantity need replenishment or investigation.",
        count: outOfStockVariants,
        tone: "rose",
        onClick: () => setStatusFilter("out_of_stock"),
        actionLabel: "Filter stock levels",
      } : null,
      pendingReceipts.length ? {
        id: "inventory-pending-receipts",
        title: "Delivered shipments awaiting receipt",
        description: "Supplier deliveries have arrived but business receipt posting is still open.",
        count: pendingReceipts.length,
        tone: "blue",
        onClick: () => setShowReceive(true),
        actionLabel: "Record receipt",
      } : null,
      discrepancyReceipts.length ? {
        id: "inventory-discrepancies",
        title: "Receipts with discrepancy",
        description: "Some deliveries were received with rejected or discrepant quantities.",
        count: discrepancyReceipts.length,
        tone: "rose",
        onClick: () => { setTab("receipts"); setPage(1); },
        actionLabel: "Open receipt history",
      } : null,
    ].filter(Boolean);
  }, [deliveryOpenWork, stockLevels]);

  const resetReceiveModal = useCallback(() => {
    setShowReceive(false);
    setReceiveForm({ deliveryId: "", purchaseOrderId: "", notes: "" });
    setReceiveLines([]);
    setSelectedDeliveryDetail(null);
    setLoadingReceiveDetail(false);
  }, []);

  const handleSelectDelivery = useCallback(async (deliveryId) => {
    setReceiveForm((current) => ({
      ...current,
      deliveryId,
      purchaseOrderId: "",
      notes: current.deliveryId === deliveryId ? current.notes : "",
    }));
    setReceiveLines([]);
    setSelectedDeliveryDetail(null);

    if (!deliveryId) return;

    setLoadingReceiveDetail(true);
    try {
      const res = await logisticsAPI.getById(Number(deliveryId));
      const delivery = res?.data || res || null;
      setSelectedDeliveryDetail(delivery);
      setReceiveForm((current) => ({
        ...current,
        deliveryId,
        purchaseOrderId: String(delivery?.purchase_order_id || ""),
      }));
      setReceiveLines(buildReceiveLines(delivery));
    } catch (err) {
      setSelectedDeliveryDetail(null);
      setReceiveLines([]);
      Swal.fire({
        ...swalBase,
        icon: "error",
        title: "Delivery load failed",
        html: err?.message || "Unable to load delivery receipt lines.",
        confirmButtonText: "OK",
      });
    } finally {
      setLoadingReceiveDetail(false);
    }
  }, [swalBase]);

  const updateReceiveLine = useCallback((deliveryLineId, updater) => {
    setReceiveLines((current) =>
      current.map((line) => {
        if (line.deliveryLineId !== deliveryLineId) return line;
        const patch = typeof updater === "function" ? updater(line) : updater;
        const { __changedField = null, ...cleanPatch } = patch || {};
        return normalizeReceiveLineDraft(line, cleanPatch, __changedField);
      }),
    );
  }, []);

  const applyReceivePreset = useCallback((mode) => {
    setReceiveLines((current) => current.map((line) =>
      mode === "clear"
        ? buildReceiveQuantityPreset(line, 0)
        : buildReceiveQuantityPreset(line, line.outstandingQuantity)
    ));
  }, []);

  const handleAdjust = async () => {
    const qty = Number(adjustForm.adjustmentQty);
    const reason = String(adjustForm.reason || "").trim();
    let displayLabel, currentQty;
    if (adjustContext) {
      displayLabel = adjustContext.variantTitle;
      currentQty = adjustContext.quantity;
    } else {
      const sv = productVariants.find((v) => String(v.id) === String(adjustForm.variantId));
      displayLabel = sv ? (adjustVariantLabel(sv) || sv.title || sv.sku || `#${sv.id}`) : "N/A";
      currentQty = sv?.quantity ?? "?";
    }

    if (!reason) {
      Swal.fire({
        ...swalBase,
        icon: "warning",
        title: "Reason required",
        html: "Enter a reason before applying a stock adjustment.",
        confirmButtonText: "OK",
      });
      return;
    }

    const { isConfirmed } = await Swal.fire({
      ...swalBase,
      icon: "warning",
      title: "Confirm Stock Adjustment",
      html: `<div class="text-left text-sm space-y-1">
        <p><strong>Variant:</strong> ${displayLabel}</p>
        <p><strong>Current stock:</strong> ${currentQty}</p>
        <p><strong>Adjustment:</strong> ${qty > 0 ? "+" : ""}${qty}</p>
        <p><strong>New stock:</strong> ${Math.max(0, Number(currentQty) + qty)}</p>
        <p><strong>Reason:</strong> ${reason}</p>
      </div>`,
      showCancelButton: true,
      confirmButtonText: "Apply Adjustment",
      cancelButtonText: "Cancel",
    });
    if (!isConfirmed) return;

    setAdjusting(true);
    try {
      await inventoryAPI.adjustStock({
        productId: Number(adjustForm.productId),
        variantId: adjustForm.variantId ? Number(adjustForm.variantId) : null,
        adjustmentQty: qty,
        reason,
      });
      setShowAdjust(false);
      setAdjustForm({ productId: "", variantId: "", adjustmentQty: "", reason: "" });
      setAdjustContext(null);
      fetchData();
      Swal.fire({ ...swalBase, icon: "success", title: "Stock adjusted", timer: 1500, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ ...swalBase, icon: "error", title: "Adjustment failed", html: e?.message || "Something went wrong", confirmButtonText: "OK" });
    } finally {
      setAdjusting(false);
    }
  };

  const handleReceive = async () => {
    const payloadLines = receiveLines
      .map((line) => ({
        ...line,
        receivedQuantity: Number(line.receivedQuantity || 0),
        acceptedQuantity: Number(line.acceptedQuantity || 0),
        rejectedQuantity: Number(line.rejectedQuantity || 0),
        discrepancyReason: String(line.discrepancyReason || "").trim(),
      }))
      .filter((line) => line.receivedQuantity > 0);

    if (!receiveForm.deliveryId || !payloadLines.length) {
      await Swal.fire({
        ...swalBase,
        icon: "warning",
        title: "Nothing to receive yet",
        html: "Choose a delivered shipment and enter at least one received line quantity.",
        confirmButtonText: "OK",
      });
      return;
    }

    if (selectedDeliveryDetail?.status !== "DELIVERED") {
      await Swal.fire({
        ...swalBase,
        icon: "warning",
        title: "Delivery not ready",
        html: "Receipt can only be recorded after the supplier marks the delivery as delivered.",
        confirmButtonText: "OK",
      });
      return;
    }

    const invalidLine = payloadLines.find((line) =>
      line.receivedQuantity < 1
      || line.acceptedQuantity < 0
      || line.rejectedQuantity < 0
      || line.receivedQuantity > line.outstandingQuantity
      || line.acceptedQuantity + line.rejectedQuantity !== line.receivedQuantity
      || (line.rejectedQuantity > 0 && !line.discrepancyReason)
    );
    if (invalidLine) {
      await Swal.fire({
        ...swalBase,
        icon: "warning",
        title: "Invalid receiving quantities",
        html: "Received quantity cannot exceed the outstanding quantity, accepted plus rejected must exactly equal received, and rejected quantities require a discrepancy reason.",
        confirmButtonText: "OK",
      });
      return;
    }

    const { isConfirmed } = await Swal.fire({
      ...swalBase,
      icon: "question",
      title: "Confirm Business Receipt",
      html: `<div class="text-left text-sm space-y-1">
        <p><strong>Delivery:</strong> ${getDeliveryLabel(selectedDeliveryDetail || { id: receiveForm.deliveryId })}</p>
        <p><strong>Lines to receive:</strong> ${payloadLines.length}</p>
        <p><strong>Total received:</strong> ${payloadLines.reduce((sum, line) => sum + line.receivedQuantity, 0)}</p>
        <p><strong>Total accepted:</strong> ${payloadLines.reduce((sum, line) => sum + line.acceptedQuantity, 0)}</p>
        <p><strong>Total rejected:</strong> ${payloadLines.reduce((sum, line) => sum + line.rejectedQuantity, 0)}</p>
      </div>`,
      showCancelButton: true,
      confirmButtonText: "Confirm Receipt",
      cancelButtonText: "Cancel",
    });
    if (!isConfirmed) return;

    setReceiving(true);
    try {
      await inventoryAPI.receiveStock({
        deliveryId: receiveForm.deliveryId ? Number(receiveForm.deliveryId) : null,
        purchaseOrderId: receiveForm.purchaseOrderId ? Number(receiveForm.purchaseOrderId) : null,
        notes: receiveForm.notes,
        lines: payloadLines.map((line) => ({
          deliveryLineId: line.deliveryLineId,
          purchaseOrderLineId: line.purchaseOrderLineId,
          productId: line.productId,
          variantId: line.variantId ? Number(line.variantId) : null,
          expectedQuantity: line.expectedQuantity,
          receivedQuantity: line.receivedQuantity,
          acceptedQuantity: line.acceptedQuantity,
          rejectedQuantity: line.rejectedQuantity,
          discrepancyReason: line.discrepancyReason || null,
          notes: line.note || receiveForm.notes || null,
        })),
      });
      resetReceiveModal();
      fetchData();
      Swal.fire({ ...swalBase, icon: "success", title: "Receipt recorded", timer: 1500, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ ...swalBase, icon: "error", title: "Receive failed", html: e?.message || "Something went wrong", confirmButtonText: "OK" });
    } finally {
      setReceiving(false);
    }
  };

  // Open adjust modal pre-filled for a specific row from stock levels
  const openAdjustForRow = (row) => {
    setAdjustForm({
      productId: String(row.product_id),
      variantId: String(row.variant_id),
      adjustmentQty: "",
      reason: "",
    });
    setAdjustContext({
      productName: row.product_name,
      variantTitle: row.variant_title || "Default",
      sku: row.sku,
      quantity: row.quantity ?? 0,
    });
    setShowAdjust(true);
  };

  const MOVEMENT_COLUMNS = useMemo(() => [
    { key: "id", label: "#", className: "font-mono text-xs" },
    { key: "product_name", label: "Product", sortable: true },
    {
      key: "movement_type", label: "Type",
      render: (r) => {
        const map = {
          INBOUND: { label: "Inbound", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" },
          OUTBOUND: { label: "Outbound", cls: "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200" },
          ADJUSTMENT: { label: "Adjustment", cls: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" },
          RESERVED: { label: "Reserved", cls: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200" },
        };
        const m = map[r.movement_type] || { label: r.movement_type, cls: "bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300" };
        return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.cls}`}>{m.label}</span>;
      },
    },
    { key: "quantity", label: "Qty", className: "tabular-nums", render: (r) => <span className={r.quantity > 0 ? "text-emerald-600" : "text-rose-600"}>{r.quantity > 0 ? "+" : ""}{r.quantity}</span> },
    { key: "reference_type", label: "Ref Type", render: (r) => r.reference_type || "—" },
    { key: "reason", label: "Reason", render: (r) => r.reason || "—" },
    { key: "created_at", label: "Date", sortable: true, render: (r) => r.created_at ? new Date(r.created_at).toLocaleString() : "—" },
  ], []);

  const RECEIPT_COLUMNS = useMemo(() => [
    { key: "receipt_number", label: "Receipt", className: "font-mono text-xs", render: (r) => r.receipt_number || `#${r.id}` },
    { key: "product_name", label: "Product" },
    { key: "variant_title", label: "Variant", render: (r) => r.variant_title || "Default" },
    { key: "expected_quantity", label: "Expected", className: "tabular-nums", render: (r) => r.expected_quantity ?? r.ordered_quantity ?? 0 },
    { key: "received_quantity", label: "Received", className: "tabular-nums" },
    { key: "accepted_quantity", label: "Accepted", className: "tabular-nums", render: (r) => r.accepted_quantity ?? r.received_quantity ?? 0 },
    { key: "rejected_quantity", label: "Rejected", className: "tabular-nums", render: (r) => r.rejected_quantity ?? r.damaged_quantity ?? 0 },
    { key: "notes", label: "Notes", render: (r) => r.notes || "—" },
    { key: "discrepancy_reason", label: "Discrepancy", render: (r) => r.discrepancy_reason || "—" },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "created_at", label: "Date", render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : "—" },
  ], []);

  // Stock levels columns with row-action removed — now using grouped table layout

  const tabClass = (t) => [
    "px-4 py-2 text-sm font-medium rounded-t-lg transition",
    t === tab
      ? "bg-white text-neutral-900 border border-b-0 border-neutral-200 dark:bg-neutral-900 dark:text-neutral-50 dark:border-neutral-700"
      : "text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200",
  ].join(" ");

  const resolvedPanelTitle = historyTitle || (
    tab === "levels"
      ? "Stock Levels"
      : tab === "movements"
        ? movementTypeFilter === "ADJUSTMENT"
          ? "Adjustment History"
          : "Movement History"
        : "Receipt History"
  );

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Inventory" title={title} subtitle={subtitle}>
        {showAdjustAction ? (
          <BtnSecondary onClick={() => { setAdjustContext(null); setAdjustForm({ productId: "", variantId: "", adjustmentQty: "", reason: "" }); setShowAdjust(true); }}>
            <ArrowUpDown className="h-4 w-4" /> Adjust Stock
          </BtnSecondary>
        ) : null}
        {showReceiveAction ? (
          <BtnPrimary onClick={() => setShowReceive(true)}>
            <ArrowDownToLine className="h-4 w-4" /> Record Receipt
          </BtnPrimary>
        ) : null}
      </PageHeader>

      {showOpenWork ? (
        <OpenWorkPanel
          title="Inventory Open Work"
          subtitle="Operational inventory issues that still need replenishment, receipt posting, or follow-up."
          items={inventoryOpenWorkItems}
          emptyTitle="Inventory work queue is clear"
          emptyDescription="No low-stock, out-of-stock, pending-receipt, or discrepancy items are currently open."
        />
      ) : null}

      {/* Tab bar */}
      {!lockedTab ? (
        <div className="flex gap-1 border-b border-neutral-200 dark:border-neutral-700">
          <button className={tabClass("levels")} onClick={() => { setTab("levels"); setPage(1); }}>
            <span className="flex items-center gap-1.5"><Package className="h-3.5 w-3.5" /> Stock Levels</span>
          </button>
          <button className={tabClass("movements")} onClick={() => { setTab("movements"); setPage(1); }}>Stock Movements</button>
          <button className={tabClass("receipts")} onClick={() => { setTab("receipts"); setPage(1); }}>Stock Receipts</button>
        </div>
      ) : null}

      {/* Stock Levels search/filter bar */}
      {tab === "levels" && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              value={searchQ}
              onChange={(e) => { setSearchQ(e.target.value); setPage(1); }}
              placeholder="Search by product name, SKU, or variant..."
              className={inputCls + " pl-9"}
            />
          </div>
          <ShopSelect
            value={statusFilter}
            onValueChange={(value) => { setStatusFilter(value); setPage(1); }}
            options={[
              { value: "", label: "All statuses" },
              { value: "out_of_stock", label: "Out of Stock" },
              { value: "low_stock", label: "Low Stock" },
              { value: "in_stock", label: "In Stock" },
            ]}
            ariaLabel="Filter stock by status"
            align="start"
            minPanelWidth={192}
            triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm sm:w-48"
            panelClassName="py-1"
          />
        </div>
      )}

      <Panel title={resolvedPanelTitle}>
        {tab === "levels" ? (
          <div className="overflow-x-auto">
            {loading ? (
              <div className="space-y-2 px-4 py-6">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-10 animate-pulse rounded bg-neutral-200 dark:bg-neutral-800" />
                ))}
              </div>
            ) : groupedStock.length === 0 ? (
              <p className="px-4 py-12 text-center text-neutral-500 dark:text-neutral-400">No inventory records found.</p>
            ) : (
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 dark:border-neutral-700">
                    {["Product / Variant", "SKU", "On Hand", "Reserved", "Incoming", "Available", "Status", ""].map((h) => (
                      <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {groupedStock.map((group) => {
                    const expanded = expandedProducts.has(group.product_id);
                    const totalAvail = group.totalQty - group.totalReserved;
                    const groupSummary = deriveGroupStockSummary(group);
                    return (
                      <Fragment key={group.product_id}>
                        {/* Product group header */}
                        <tr
                          className="cursor-pointer bg-neutral-50 hover:bg-neutral-100 dark:bg-neutral-800/40 dark:hover:bg-neutral-800/70"
                          onClick={() => toggleProduct(group.product_id)}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {expanded ? <ChevronDown className="h-4 w-4 text-neutral-400" /> : <ChevronRight className="h-4 w-4 text-neutral-400" />}
                              <div>
                                <span className="font-semibold text-neutral-900 dark:text-neutral-50">{group.product_name}</span>
                                {group.product_status === "DRAFT" && (
                                  <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">Draft</span>
                                )}
                                <div className="text-xs text-neutral-500 dark:text-neutral-400">
                                  {group.variants.length} variant{group.variants.length !== 1 ? "s" : ""} | {describeGroupStockSummary(groupSummary)}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3" />
                          <td className="px-4 py-3 tabular-nums font-semibold">{group.totalQty}</td>
                          <td className="px-4 py-3 tabular-nums">{group.totalReserved}</td>
                          <td className="px-4 py-3 tabular-nums">{group.totalIncoming}</td>
                          <td className="px-4 py-3 tabular-nums font-semibold">
                            <span className={totalAvail > 0 ? "text-emerald-600" : "text-rose-600"}>{totalAvail}</span>
                          </td>
                          <td className="px-4 py-3">
                            <StockStatusBadge quantity={group.totalQty} threshold={group.low_stock_threshold} status={groupSummary.status} />
                          </td>
                          <td className="px-4 py-3">
                            {groupSummary.status !== "in_stock" && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); createPRForProduct(group.product_id); }}
                                className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-300 dark:hover:bg-blue-900/50"
                                title="Create purchase requisition for this product"
                              >
                                <ShoppingCart className="h-3 w-3" /> Create PR
                              </button>
                            )}
                          </td>
                        </tr>
                        {/* Variant child rows */}
                        {expanded && group.variants.map((v) => {
                          const avail = (v.quantity ?? 0) - (v.reserved ?? 0);
                          return (
                            <tr key={v.variant_id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/30">
                              <td className="py-2.5 pl-12 pr-4">
                                <span className="text-neutral-700 dark:text-neutral-200">{v.variant_title || "Default"}</span>
                              </td>
                              <td className="px-4 py-2.5 font-mono text-xs">{v.sku || "—"}</td>
                              <td className="px-4 py-2.5 tabular-nums font-semibold">{v.quantity ?? 0}</td>
                              <td className="px-4 py-2.5 tabular-nums">{v.reserved ?? 0}</td>
                              <td className="px-4 py-2.5 tabular-nums">{v.incoming ?? 0}</td>
                              <td className="px-4 py-2.5 tabular-nums font-semibold">
                                <span className={avail > 0 ? "text-emerald-600" : "text-rose-600"}>{avail}</span>
                              </td>
                              <td className="px-4 py-2.5">
                                <StockStatusBadge quantity={v.quantity ?? 0} threshold={v.low_stock_threshold ?? 5} />
                              </td>
                              <td className="px-4 py-2.5">
                                <div className="flex gap-1">
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); openAdjustForRow(v); }}
                                    className="rounded-lg border border-neutral-200 bg-white px-2.5 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700"
                                  >
                                    Adjust
                                  </button>
                                  {(v.quantity ?? 0) <= (v.low_stock_threshold ?? group.low_stock_threshold ?? 5) && (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); createPRForProduct(v.product_id, v.variant_id); }}
                                      className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-300 dark:hover:bg-blue-900/50"
                                      title="Create purchase requisition"
                                    >
                                      <ShoppingCart className="h-3 w-3" /> PR
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <DataTable
            columns={tab === "movements" ? MOVEMENT_COLUMNS : RECEIPT_COLUMNS}
            data={tab === "movements" ? movements : receipts}
            loading={loading}
            emptyMessage={tab === "movements" ? "No stock movements recorded." : "No stock receipts recorded."}
          />
        )}
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </Panel>

      {/* Adjust Stock Modal */}
      <FormModal open={showAdjust} onClose={() => { setShowAdjust(false); setAdjustContext(null); }} title="Adjust Stock"
        footer={
          <>
            <BtnSecondary onClick={() => { setShowAdjust(false); setAdjustContext(null); }}>Cancel</BtnSecondary>
            <BtnPrimary onClick={handleAdjust} disabled={adjusting || !adjustForm.productId || !adjustForm.adjustmentQty || !String(adjustForm.reason || "").trim()}>
              {adjusting ? "Adjusting..." : "Apply Adjustment"}
            </BtnPrimary>
          </>
        }
      >
        <div className="space-y-4">
          {adjustContext ? (
            <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-700 dark:bg-neutral-800/50">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                <div>
                  <span className="text-neutral-500 dark:text-neutral-400">Product</span>
                  <p className="font-medium text-neutral-900 dark:text-neutral-100">{adjustContext.productName}</p>
                </div>
                <div>
                  <span className="text-neutral-500 dark:text-neutral-400">Variant</span>
                  <p className="font-medium text-neutral-900 dark:text-neutral-100">{adjustContext.variantTitle}</p>
                </div>
                {adjustContext.sku && (
                  <div>
                    <span className="text-neutral-500 dark:text-neutral-400">SKU</span>
                    <p className="font-mono text-neutral-900 dark:text-neutral-100">{adjustContext.sku}</p>
                  </div>
                )}
                <div>
                  <span className="text-neutral-500 dark:text-neutral-400">Current Stock</span>
                  <p className="font-semibold text-neutral-900 dark:text-neutral-100">{adjustContext.quantity}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Product *</label>
                <ShopSelect value={adjustForm.productId} onValueChange={(value) => setAdjustForm((current) => ({ ...current, productId: value, variantId: "" }))} options={[{ value: "", label: "Select a product..." }, ...allProducts.map((product) => ({ value: product.id, label: `${product.name}${product.sku ? ` (${product.sku})` : ""}` }))]} ariaLabel="Product" align="start" minPanelWidth={240} triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm" panelClassName="py-1" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Variant *</label>
                <ShopSelect value={adjustForm.variantId} onValueChange={(value) => setAdjustForm((current) => ({ ...current, variantId: value }))} options={[{ value: "", label: adjustForm.productId ? (productVariants.length ? "Select a variant..." : "Loading...") : "Select product first" }, ...productVariants.map((variant) => { const label = adjustVariantLabel(variant) || variant.title || variant.sku || `Variant #${variant.id}`; return { value: variant.id, label: `${label}${variant.sku ? ` (${variant.sku})` : ""} - Qty: ${variant.quantity ?? 0}` }; })]} disabled={!adjustForm.productId} ariaLabel="Variant" align="start" minPanelWidth={240} triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm" panelClassName="py-1" />
              </div>
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Adjustment Quantity * (negative to decrease)</label>
            <input type="number" value={adjustForm.adjustmentQty} onChange={(e) => setAdjustForm((f) => ({ ...f, adjustmentQty: e.target.value }))}
              className={inputCls}
              placeholder="e.g., 10 or -5" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Reason *</label>
            <textarea rows={2} value={adjustForm.reason} onChange={(e) => setAdjustForm((f) => ({ ...f, reason: e.target.value }))}
              className={inputCls}
              placeholder="Reason for adjustment" />
          </div>
        </div>
      </FormModal>

      <FormModal open={showReceive} onClose={resetReceiveModal} title="Record Business Receipt" size="xl"
        footer={
          <>
            <BtnSecondary onClick={resetReceiveModal}>Cancel</BtnSecondary>
            <BtnPrimary onClick={handleReceive} disabled={receiving || !receiveForm.deliveryId || receiveSummary.lineCount === 0}>
              {receiving ? "Recording..." : "Record Receipt"}
            </BtnPrimary>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Delivered Shipment</label>
            <ShopSelect
              value={receiveForm.deliveryId}
              onValueChange={handleSelectDelivery}
              options={[
                { value: "", label: "Select a delivered shipment..." },
                ...deliveredList.map((delivery) => ({ value: delivery.id, label: `${getDeliveryLabel(delivery)} | ${getPOLabel(delivery)} | ${delivery.product_name || "Product"} | ${delivery.receiving_status || "PENDING_RECEIPT"}` })),
              ]}
              ariaLabel="Delivered Shipment"
              align="start"
              minPanelWidth={320}
              triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm"
              panelClassName="py-1"
            />
            {!deliveredList.length && (
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                No delivered shipments are ready for receiving yet.
              </p>
            )}
          </div>

          {loadingReceiveDetail && (
            <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-500 dark:border-neutral-700 dark:bg-neutral-800/50 dark:text-neutral-300">
              Loading delivery receipt lines...
            </div>
          )}

          {selectedDeliveryDetail && !loadingReceiveDetail && (
            <>
              <div className="grid gap-3 rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-xs sm:grid-cols-4 dark:border-neutral-700 dark:bg-neutral-800/50">
                <div>
                  <p className="text-neutral-500 dark:text-neutral-400">PO</p>
                  <p className="font-semibold text-neutral-900 dark:text-neutral-100">{getPOLabel(selectedDeliveryDetail)}</p>
                </div>
                <div>
                  <p className="text-neutral-500 dark:text-neutral-400">Supplier</p>
                  <p className="font-semibold text-neutral-900 dark:text-neutral-100">{selectedDeliveryDetail.supplier_name || "-"}</p>
                </div>
                <div>
                  <p className="text-neutral-500 dark:text-neutral-400">Delivery Status</p>
                  <p className="font-semibold text-neutral-900 dark:text-neutral-100">{selectedDeliveryDetail.status || "-"}</p>
                </div>
                <div>
                  <p className="text-neutral-500 dark:text-neutral-400">Receiving Status</p>
                  <p className="font-semibold text-neutral-900 dark:text-neutral-100">{selectedDeliveryDetail.receiving_status || "PENDING_RECEIPT"}</p>
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-neutral-200 dark:border-neutral-700">
                <div className="flex items-center justify-between gap-3 border-b border-neutral-200 bg-neutral-50 px-3 py-2 text-xs dark:border-neutral-700 dark:bg-neutral-900/60">
                  <p className="text-neutral-500 dark:text-neutral-400">
                    Lines default to the full outstanding quantity. Adjust only if the actual receipt differs.
                  </p>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => applyReceivePreset("full")} className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5 font-medium text-neutral-700 transition hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700">
                      Receive Full Outstanding
                    </button>
                    <button type="button" onClick={() => applyReceivePreset("clear")} className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5 font-medium text-neutral-700 transition hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700">
                      Clear All
                    </button>
                  </div>
                </div>
                <table className="w-full text-xs">
                  <thead className="text-neutral-500 dark:text-neutral-400">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Variant</th>
                      <th className="px-3 py-2 text-left font-medium">SKU</th>
                      <th className="px-3 py-2 text-right font-medium">Expected</th>
                      <th className="px-3 py-2 text-right font-medium">Received to Date</th>
                      <th className="px-3 py-2 text-right font-medium">Outstanding</th>
                      <th className="px-3 py-2 text-right font-medium">Receive Now</th>
                      <th className="px-3 py-2 text-right font-medium">Accept</th>
                      <th className="px-3 py-2 text-right font-medium">Reject</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receiveLines.map((line) => (
                      <Fragment key={line.deliveryLineId}>
                        <tr className="border-t border-neutral-100 align-top dark:border-neutral-800">
                          <td className="px-3 py-3">
                            <div className="font-medium text-neutral-800 dark:text-neutral-100">{line.label}</div>
                          </td>
                          <td className="px-3 py-3 font-mono text-[11px] text-neutral-500 dark:text-neutral-400">{line.sku || "-"}</td>
                          <td className="px-3 py-3 text-right text-neutral-700 dark:text-neutral-200">{line.expectedQuantity}</td>
                          <td className="px-3 py-3 text-right text-neutral-700 dark:text-neutral-200">{line.receivedToDate}</td>
                          <td className="px-3 py-3 text-right font-semibold text-neutral-800 dark:text-neutral-100">{line.outstandingQuantity}</td>
                          <td className="px-3 py-3">
                            <input
                              type="number"
                              min="0"
                              max={line.outstandingQuantity}
                              value={line.receivedQuantity}
                              onChange={(e) => {
                                const nextReceived = toWholeNumberInput(e.target.value);
                                updateReceiveLine(line.deliveryLineId, {
                                  __changedField: "receivedQuantity",
                                  receivedQuantity: nextReceived,
                                });
                              }}
                              disabled={line.outstandingQuantity <= 0}
                              className="w-24 rounded-lg border border-neutral-200 px-2 py-1.5 text-right text-xs outline-none focus:ring-2 focus:ring-blue-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
                            />
                          </td>
                          <td className="px-3 py-3">
                            <input
                              type="number"
                              min="0"
                              max={line.receivedQuantity || line.outstandingQuantity}
                              value={line.acceptedQuantity}
                              onChange={(e) => updateReceiveLine(line.deliveryLineId, { __changedField: "acceptedQuantity", acceptedQuantity: toWholeNumberInput(e.target.value) })}
                              disabled={!line.receivedQuantity}
                              className="w-24 rounded-lg border border-neutral-200 px-2 py-1.5 text-right text-xs outline-none focus:ring-2 focus:ring-emerald-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
                            />
                          </td>
                          <td className="px-3 py-3">
                            <input
                              type="number"
                              min="0"
                              max={line.receivedQuantity || line.outstandingQuantity}
                              value={line.rejectedQuantity}
                              onChange={(e) => updateReceiveLine(line.deliveryLineId, { __changedField: "rejectedQuantity", rejectedQuantity: toWholeNumberInput(e.target.value) })}
                              disabled={!line.receivedQuantity}
                              className="w-24 rounded-lg border border-neutral-200 px-2 py-1.5 text-right text-xs outline-none focus:ring-2 focus:ring-amber-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
                            />
                          </td>
                        </tr>
                        <tr className="border-b border-neutral-100 dark:border-neutral-800">
                          <td colSpan={8} className="px-3 pb-3">
                            <div className="grid gap-3 sm:grid-cols-2">
                              <input
                                type="text"
                                value={line.discrepancyReason}
                                onChange={(e) => updateReceiveLine(line.deliveryLineId, { discrepancyReason: e.target.value })}
                                className={inputCls}
                                placeholder="Discrepancy reason if any quantity is rejected"
                                disabled={Number(line.rejectedQuantity || 0) === 0}
                              />
                              <input
                                type="text"
                                value={line.note}
                                onChange={(e) => updateReceiveLine(line.deliveryLineId, { note: e.target.value })}
                                className={inputCls}
                                placeholder="Optional receiving note for this line"
                              />
                            </div>
                          </td>
                        </tr>
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-xs sm:grid-cols-4 dark:border-blue-800 dark:bg-blue-950/20">
                <div>
                  <p className="text-blue-600 dark:text-blue-400">Lines entered</p>
                  <p className="font-semibold text-blue-900 dark:text-blue-100">{receiveSummary.lineCount}</p>
                </div>
                <div>
                  <p className="text-blue-600 dark:text-blue-400">Received now</p>
                  <p className="font-semibold text-blue-900 dark:text-blue-100">{receiveSummary.received}</p>
                </div>
                <div>
                  <p className="text-blue-600 dark:text-blue-400">Accepted now</p>
                  <p className="font-semibold text-blue-900 dark:text-blue-100">{receiveSummary.accepted}</p>
                </div>
                <div>
                  <p className="text-blue-600 dark:text-blue-400">Rejected now</p>
                  <p className="font-semibold text-blue-900 dark:text-blue-100">{receiveSummary.rejected}</p>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Receipt Notes</label>
                <textarea
                  rows={2}
                  value={receiveForm.notes}
                  onChange={(e) => setReceiveForm((current) => ({ ...current, notes: e.target.value }))}
                  className={inputCls}
                  placeholder="Optional note applied to receipt lines that do not have their own note"
                />
              </div>
            </>
          )}
        </div>
      </FormModal>

    </div>
  );
}
